/**
 * A hash router in thirty lines, rather than a routing dependency.
 *
 * The Mini App navigates by component state and needs nothing more — it is one
 * flow on a phone with a native back button. This console is different: an
 * operator investigating a deposit wants to paste the URL into a chat and have
 * a colleague land on the same screen, which is exactly what a second reviewer
 * needs under two-person review. That requires real URLs.
 *
 * The hash, not the path, because these are static files: a path route would
 * 404 on refresh unless whatever serves them rewrites unknown paths to
 * `index.html`, and quietly depending on a server rewrite is how a console
 * works in dev and breaks the first time it is deployed.
 */

import { useEffect, useState } from 'react';

export type Route =
  | { name: 'dashboard' }
  /** `tab` omitted (or 'pending') is the review queue; 'all' shows every deposit ever, decided or not. */
  | { name: 'deposits'; tab?: 'pending' | 'all' }
  | { name: 'deposit'; id: string }
  | { name: 'withdrawals'; tab?: 'pending' | 'all' }
  | { name: 'withdrawal'; id: string }
  | { name: 'rounds'; slug?: string }
  | { name: 'round'; id: string }
  | { name: 'gameTypes'; slug?: string }
  | { name: 'destinations' }
  | { name: 'agents' }
  | { name: 'posters' }
  | { name: 'admins' }
  | { name: 'reconcile' }
  /** `tab` omitted (or 'winners') is the default landing tab; 'all' is the full directory; 'banned' shows suspended players. */
  | { name: 'players'; tab?: 'winners' | 'all' | 'banned' | 'phantom' }
  | { name: 'player'; id: string }
  | { name: 'ipAllowlist' }
  | { name: 'inbox' }

export function parseHash(hash: string): Route {
  const [name, id] = hash.replace(/^#\/?/, '').split('/');
  switch (name) {
    case 'dashboard':
      return { name: 'dashboard' };
    case 'deposit':
      return id ? { name: 'deposit', id } : { name: 'deposits' };
    case 'withdrawals':
      return id === 'all' ? { name: 'withdrawals', tab: 'all' } : { name: 'withdrawals' };
    case 'withdrawal':
      return id ? { name: 'withdrawal', id } : { name: 'withdrawals' };
    case 'rounds':
      return id ? { name: 'rounds', slug: id } : { name: 'rounds' };
    case 'round':
      return id ? { name: 'round', id } : { name: 'rounds' };
    case 'game-types':
      return id ? { name: 'gameTypes', slug: id } : { name: 'gameTypes' };
    case 'destinations':
      return { name: 'destinations' };
    case 'agents':
      return { name: 'agents' };
    case 'posters':
      return { name: 'posters' };
    case 'admins':
      return { name: 'admins' };
    case 'reconcile':
      return { name: 'reconcile' };
    case 'players':
      if (id === 'all') return { name: 'players', tab: 'all' };
      if (id === 'banned') return { name: 'players', tab: 'banned' };
      if (id === 'phantom') return { name: 'players', tab: 'phantom' };
      return { name: 'players' };
    case 'player':
      return id ? { name: 'player', id } : { name: 'players' };
    case 'ip-allowlist':
      return { name: 'ipAllowlist' };
    case 'inbox':
      return { name: 'inbox' };
    case 'deposits':
      return id === 'all' ? { name: 'deposits', tab: 'all' } : { name: 'deposits' };
    default:
      // The deposit queue is the landing page: it is the screen with work
      // waiting on it, and the one people open the console to reach.
      return { name: 'deposits' };
  }
}

export function useRoute(): Route {
  const [route, setRoute] = useState<Route>(() => parseHash(window.location.hash));
  useEffect(() => {
    const onChange = (): void => setRoute(parseHash(window.location.hash));
    window.addEventListener('hashchange', onChange);
    return () => window.removeEventListener('hashchange', onChange);
  }, []);
  return route;
}

export function href(route: Route): string {
  switch (route.name) {
    case 'deposit':
    case 'withdrawal':
    case 'player':
    case 'round':
      return `#/${route.name}/${route.id}`;
    case 'gameTypes':
      return route.slug ? `#/game-types/${route.slug}` : '#/game-types';
    case 'rounds':
      return route.slug ? `#/rounds/${route.slug}` : '#/rounds';
    case 'players':
      if (route.tab === 'all') return '#/players/all';
      if (route.tab === 'banned') return '#/players/banned';
      if (route.tab === 'phantom') return '#/players/phantom';
      return '#/players';
    case 'ipAllowlist':
      return '#/ip-allowlist';
    case 'deposits':
      return route.tab === 'all' ? '#/deposits/all' : '#/deposits';
    case 'withdrawals':
      return route.tab === 'all' ? '#/withdrawals/all' : '#/withdrawals';
    default:
      return `#/${route.name}`;
  }
}

export function navigate(route: Route): void {
  window.location.hash = href(route);
}
